// src/admin/pages/AdminInventory.js
import React, { useEffect, useState, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaBoxOpen, FaTrash, FaSearch } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getInventoryAlerts,
  deleteInventoryAlert,
} from "../../services/adminInventory.api";
import { useNavigate } from "react-router-dom";
import { adjustInventoryStock } from "../../services/adminInventory.api";

function AdminInventory() {
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkQty, setBulkQty] = useState("");

  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const [selected, setSelected] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  const [filter, setFilter] = useState("all");

  const [confirmSave, setConfirmSave] = useState(false);

  const [editedQty, setEditedQty] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState(null);

  const isDirty = Object.keys(editedQty).length > 0;

  const [page, setPage] = useState(1);
  const perPage = 12;

  const TEXT = {
    EN: {
      title: "Inventory Alerts",
      search: "Search products...",
      product: "Product",
      price: "Price",
      qty: "Quantity",
      status: "Status",
      delete: "Delete",
      cancel: "Cancel",
      deleteTitle: "Delete Product?",
      deleteText: "This action cannot be undone.",
      out: "Out of stock",
      low: "Low stock",
      all: "All",
variants: "Variants",
loading: "Loading...",
noResults: "No results",
items: "items",
prev: "Prev",
next: "Next",
more: "more",
bulkEdit: "Bulk edit",
confirmSave: "Confirm Save",
confirm: "Confirm",
apply: "Apply",
bulkEditQuantity: "Bulk edit quantity",
enterQuantity: "Enter quantity",
    },
    AR: {
      title: "تنبيهات المخزون",
      search: "ابحث عن منتج...",
      product: "المنتج",
      price: "السعر",
      qty: "الكمية",
      status: "الحالة",
      delete: "حذف",
      cancel: "إلغاء",
      deleteTitle: "حذف المنتج؟",
      deleteText: "لا يمكن التراجع عن هذا الإجراء.",
      out: "نفد المخزون",
      low: "مخزون منخفض",
      all: "الكل",
variants: "المتغيرات",
loading: "جارٍ التحميل...",
noResults: "لا توجد نتائج",
items: "عنصر",
prev: "السابق",
next: "التالي",
more: "إضافي",
bulkEdit: "تعديل جماعي",
confirmSave: "تأكيد الحفظ",
confirm: "تأكيد",
apply: "تطبيق",
bulkEditQuantity: "تعديل الكمية",
enterQuantity: "أدخل الكمية",
    },
  }[lang];

  useEffect(() => {
  setPage(1);
}, [search, filter]);

  useEffect(() => {
  setLoading(true);
  getInventoryAlerts()
    .then((data) => {
      const mapped = data.map((p) => ({
        ...p,
        id: p.product_id,

        // ✅ FIX IMAGE (same philosophy as AdminProducts)
        image:
          p.image ||
          p.main_image ||
          p.product_image ||
          (Array.isArray(p.images) && p.images[0]?.url) ||
          "/no-image.png",
      }));

      setProducts(mapped);
    })
    .finally(() => setLoading(false));
}, []);

  const getStatus = (qty) => (qty === 0 ? "out" : "low");

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !search ||
      (p.name_en || "").toLowerCase().includes(q) ||
      (p.name_ar || "").toLowerCase().includes(q) ||
      (p.public_id || "").toLowerCase().includes(q);

    const status = getStatus(p.stock_qty);

    const matchesFilter =
      filter === "all" ||
      (filter === "out" && status === "out") ||
      (filter === "low" && status === "low");

    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (page - 1) * perPage,
    page * perPage
  );

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selected.length === filtered.length) {
      setSelected([]);
    } else {
      setSelected(filtered.map((p) => p.variant_id));
    }
  };

  const handleBulkEditApply = async () => {
  const qty = Number(bulkQty);
  if (isNaN(qty)) return;

  try {
    let updatedItems = [];

    for (const variantId of selected) {
      const p = products.find(x => x.variant_id === variantId);
      if (!p) continue;

      const diff = qty - p.stock_qty;
      if (diff === 0) continue;

      await adjustInventoryStock({
        variant_id: variantId,
        qty_change: diff,
        note: "bulk_edit",
      });

      updatedItems.push({
        name: lang === "AR" ? p.name_ar : p.name_en,
        color: p.color_name,
        size: p.size,
        qty,
      });

      // update UI
      setProducts(prev =>
        prev.map(item =>
          item.variant_id === variantId
            ? { ...item, stock_qty: qty }
            : item
        )
      );
    }

    setToast({ items: updatedItems });
    setTimeout(() => setToast(null), 4000);

    // reset
    setBulkEditOpen(false);
    setBulkQty("");
    setSelected([]);

  } catch (err) {
    console.error(err);
  }
};

  const handleQtyChange = (variantId, value) => {
      setEditedQty((prev) => ({
        ...prev,
        [variantId]: value,
      }));
    };

    const handleCancelChanges = () => {
  setEditedQty({});
};

    const handleBulkSave = async () => {
  const entries = Object.entries(editedQty);

  if (entries.length === 0) return;

  try {
    setSavingId("bulk");

    let updatedItems = [];

    for (const [variantId, newQtyRaw] of entries) {
      const p = products.find((x) => x.variant_id === Number(variantId));
      if (!p) continue;

      const newQty = Number(newQtyRaw);
      if (isNaN(newQty)) continue;

      const diff = newQty - p.stock_qty;
      if (diff === 0) continue;

      await adjustInventoryStock({
        variant_id: p.variant_id,
        qty_change: diff,
        note: "admin_update",
      });

      updatedItems.push({
        name: lang === "AR" ? p.name_ar : p.name_en,
        color: p.color_name,
        size: p.size,
        qty: newQty,
      });

      // update UI
      setProducts((prev) =>
        prev.map((item) =>
          item.variant_id === p.variant_id
            ? { ...item, stock_qty: newQty }
            : item
        )
      );
    }

    setEditedQty({});

    // ✅ IMPROVED TOAST
    setToast({
      items: updatedItems,
    });

    setTimeout(() => setToast(null), 4000); // ⏱ longer

  } catch (err) {
    console.error(err);
  } finally {
    setSavingId(null);
    setConfirmSave(false);
  }
};

  const confirmDelete = async () => {
    if (deleteId === "bulk") {
      await Promise.all(selected.map((id) => deleteInventoryAlert(id)));
      setProducts((prev) =>
        prev.filter((p) => !selected.includes(p.variant_id))
      );
      setSelected([]);
    } else {
      await deleteInventoryAlert(deleteId);
      setProducts((prev) =>
        prev.filter((p) => p.variant_id !== deleteId)
      );
    }

    setDeleteId(null);
  };

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full`}>
        <AdminHeader
            setIsOpen={setIsOpen}
            customCenter={
              <div
                className={`
                  hidden sm:block
                  w-full
                  sm:max-w-[500px]
                  max-w-full
                  transition-all duration-300
                  ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
                `}
              >
                <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 sm:py-[4px] space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between">

                  {/* LEFT */}
                  <div className="flex items-center gap-2 text-[13px] text-gray-200">
                    <span>
                      {lang === "AR" ? "تغييرات غير محفوظة" : "Unsaved changes"}
                    </span>
                  </div>

                  {/* RIGHT */}
                  <div className="flex gap-2 sm:gap-1.5 w-full sm:w-auto">

                    {/* DISCARD */}
                    <button
                      type="button"
                      onClick={handleCancelChanges}
                      className="
                        px-2 py-[2px]
                        text-[12px]
                        rounded-md
                        border border-[#3a3a3a]
                        bg-[#1f1f1f]
                        hover:bg-[#363636]
                        text-gray-200
                        transition
                      "
                    >
                      {lang === "AR" ? "إلغاء" : "Discard"}
                    </button>

                    {/* SAVE */}
                    <button
                      type="button"
                      onClick={() => setConfirmSave(true)}
                      className="
                        px-2 py-[2px]
                        text-[12px]
                        rounded-md
                        font-medium
                        bg-white text-black hover:bg-gray-200
                        transition
                      "
                    >
                      {lang === "AR" ? "حفظ" : "Save"}
                    </button>

                  </div>
                </div>
              </div>
            }
          />

        <main className="p-4 sm:p-6 pb-24 md:pb-6">

          {/* HEADER */}
          <div className="flex items-center gap-2 mb-5">
            <FaBoxOpen className="text-gray-900 text-[18px]" />
            <h1 className="text-[20px] font-semibold text-gray-900">
              {TEXT.title}
            </h1>
          </div>

          {/* TABLE */}
          <div className="hidden md:block bg-white border border-[#e3e3e3] rounded-[14px] shadow-[0_12px_32px_rgba(0,0,0,0.10)]">

            {/* TOP BAR (CLONED FROM PRODUCTS) */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b">

              {/* LEFT */}
              {!searchOpen ? (
                <div className="flex flex-wrap gap-2">

                  {[
                    { label: TEXT.all, value: "all" },
                    { label: TEXT.out, value: "out" },
                    { label: TEXT.low, value: "low" },
                  ].map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setFilter(f.value)}
                      className={`px-3 py-[5px] text-[13px] rounded-md ${
                        filter === f.value
                          ? "bg-gray-200"
                          : "text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3 w-full">

  <div className="
    flex items-center justify-between
    flex-1
    h-8
    px-3
    bg-white
    border border-gray-300
    rounded-lg
    shadow-sm
    focus-within:border-gray-500
    focus-within:ring-2
    focus-within:ring-gray-200
    transition
  ">

    <div className="flex items-center gap-2 flex-1">
      <FaSearch className="text-gray-400 text-sm" />
      <input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={TEXT.search}
        className="flex-1 outline-none text-sm"
      />
    </div>

    {/* ❌ CLEAR BUTTON */}
    <button
      onClick={() => {
        setSearch("");
        setSearchOpen(false);
      }}
      className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-500 hover:bg-gray-100"
    >
      ✕
    </button>

  </div>

  {/* CANCEL BUTTON */}
  <button
    onClick={() => {
      setSearch("");
      setSearchOpen(false);
    }}
    className="px-3 h-8 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium"
  >
    {TEXT.cancel}
  </button>

</div>
              )}

              {/* RIGHT */}
              <div className="flex items-center gap-2">

                  {/* DELETE */}
                  {selected.length > 0 && (
  <>
    {/* 🔥 BULK EDIT */}
    <button
      onClick={() => setBulkEditOpen(true)}
      className="
        h-9 px-3
        flex items-center gap-2
        bg-white
        border border-gray-300
        rounded-lg
        shadow-sm
        text-sm font-medium
        hover:bg-gray-50
        transition
      "
    >
       {lang === "AR" ? "تعديل جماعي" : "Bulk edit"}
    </button>

    {/* DELETE */}
    <button
      onClick={() => setDeleteId("bulk")}
      className="w-9 h-9 flex items-center justify-center border rounded-lg"
    >
      <FaTrash />
    </button>
  </>
)}

                  {/* SEARCH */}
                  {!searchOpen && (
                    <button
  onClick={() => setSearchOpen(true)}
  className="
    flex items-center justify-center gap-1
    px-2 h-9
    bg-white
    border border-gray-300
    rounded-lg
    shadow-[0_1px_0_rgba(0,0,0,0.05),0_2px_4px_rgba(0,0,0,0.06)]
    hover:bg-gray-50
    active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.08)]
    transition
  "
>
  <FaSearch className="text-gray-600 text-sm" />
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path
      d="M3 5H17M6 10H14M9 15H11"
      stroke="#4B5563"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
</button>
                  )}
                </div>
            </div>

            {/* TABLE */}
            <table
                  dir={lang === "AR" ? "rtl" : "ltr"}
                  className="w-full text-center"
                >
              <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px]">
                <tr>
                  <th className="px-3 py-2">
                    <input
  type="checkbox"
  onChange={toggleAll}
  checked={selected.length === filtered.length && filtered.length > 0}
  className="w-4 h-4 accent-gray-800 cursor-pointer"
/>
                  </th>
                  <th>{TEXT.product}</th>
                  <th>{TEXT.variants}</th>
                  <th>{TEXT.price}</th>
                  <th>{TEXT.qty}</th>
                  <th>{TEXT.status}</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
  [...Array(6)].map((_, i) => (
    <tr key={i} className="animate-pulse border-b">
      <td className="py-3">
        <div className="w-4 h-4 bg-gray-200 rounded mx-auto"></div>
      </td>

      <td className="py-3">
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
          <div className="space-y-1">
            <div className="w-24 h-3 bg-gray-200 rounded"></div>
            <div className="w-16 h-2 bg-gray-200 rounded"></div>
          </div>
        </div>
      </td>

      <td>
        <div className="w-16 h-3 bg-gray-200 rounded mx-auto"></div>
      </td>

      <td>
        <div className="w-12 h-3 bg-gray-200 rounded mx-auto"></div>
      </td>

      <td>
        <div className="w-20 h-8 bg-gray-200 rounded mx-auto"></div>
      </td>

      <td>
        <div className="w-16 h-5 bg-gray-200 rounded mx-auto"></div>
      </td>
    </tr>
  ))
) : paginated.length === 0 ? (
                 <tr>
  <td colSpan="6" className="py-10 text-center">
    <div className="flex flex-col items-center gap-2 text-gray-500">

      <FaBoxOpen className="text-2xl opacity-40" />

      <span className="text-sm">
        {TEXT.noResults}
      </span>

    </div>
  </td>
</tr>
                ) : paginated.map((p) => {
                  const status = getStatus(p.stock_qty);

                  return (
                    <tr
                      key={p.variant_id}
                      onClick={(e) => {
                          if (e.target.closest("input")) return;

                          if (!p.id) return;
                          navigate(`/admin/products/edit/${p.id}`);
                        }}
                      className={`border-b cursor-pointer hover:bg-[#f6f6f7]
                            ${editedQty[p.variant_id] !== undefined ? "bg-yellow-50" : ""}
                          `}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
  type="checkbox"
  checked={selected.includes(p.variant_id)}
  onChange={() => toggleSelect(p.variant_id)}
  className="w-4 h-4 accent-gray-800 cursor-pointer"
/>
                      </td>

                    <td className="px-2 py-1 w-[320px] max-w-[320px] text-[13px] text-gray-800">
  <div className="flex items-center justify-center gap-3 text-center max-w-[320px] mx-auto">

    {/* IMAGE */}
    <div className="w-10 h-10 rounded-md overflow-hidden border bg-gray-100 flex-shrink-0">
      <img
        src={p.image}
        alt={p.name_en}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.src = "/no-image.png";
        }}
      />
    </div>

    {/* TEXT */}
    <div className="flex flex-col items-center min-w-0 max-w-[230px] overflow-hidden">

      {/* NAME */}
      <span
        className="block w-full font-medium text-gray-900 leading-tight truncate"
        title={lang === "AR" ? p.name_ar || p.name_en : p.name_en}
      >
        {lang === "AR" ? p.name_ar || p.name_en : p.name_en}
      </span>

      {/* PUBLIC ID */}
      <span
        className="block w-full text-[11px] text-gray-400 truncate"
        title={p.public_id}
      >
        {p.public_id}
      </span>

    </div>

  </div>
</td>

                      {/* ✅ VARIANTS COLUMN */}
                      <td className="text-[12px] text-gray-600">
                        <div className="flex flex-col items-center leading-tight">
                          <span>{p.color_name || "-"}</span>
                          <span className="text-gray-400">{p.size || "-"}</span>
                        </div>
                      </td>

                      <td className="px-3 py-1 text-center">
  <div className="flex items-center justify-center">

    <span className="
      text-[13px]
      font-semibold
      text-gray-900
      tracking-tight
    ">
      ${Number(p.base_price).toFixed(2)}
    </span>

  </div>
</td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center">
                          <div
                            className={`
                              inline-flex items-center
                              h-[34px]
                              rounded-[10px]
                              border
                              overflow-hidden
                              shadow-sm
                              bg-white
                              ${
                                editedQty[p.variant_id] !== undefined
                                  ? "border-amber-400 ring-2 ring-amber-100"
                                  : "border-gray-300"
                              }
                            `}
                          >
                            {/* MINUS */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();

                                const current =
                                  editedQty[p.variant_id] !== undefined
                                    ? Number(editedQty[p.variant_id] || 0)
                                    : Number(p.stock_qty || 0);

                                handleQtyChange(p.variant_id, Math.max(0, current - 1));
                              }}
                              className="
                                w-8 h-full
                                flex items-center justify-center
                                text-gray-600
                                bg-[#f6f6f7]
                                hover:bg-gray-200
                                active:bg-gray-300
                                transition
                                border-r border-gray-300
                              "
                            >
                              −
                            </button>

                            {/* INPUT */}
                            <input
                              type="number"
                              min="0"
                              value={
                                editedQty[p.variant_id] !== undefined
                                  ? editedQty[p.variant_id]
                                  : p.stock_qty
                              }
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleQtyChange(p.variant_id, e.target.value)}
                              className="
                                w-[56px] h-full
                                text-center text-[13px] font-medium
                                text-gray-900
                                bg-white
                                outline-none
                                [appearance:textfield]
                                [&::-webkit-outer-spin-button]:appearance-none
                                [&::-webkit-inner-spin-button]:appearance-none
                              "
                            />

                            {/* PLUS */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();

                                const current =
                                  editedQty[p.variant_id] !== undefined
                                    ? Number(editedQty[p.variant_id] || 0)
                                    : Number(p.stock_qty || 0);

                                handleQtyChange(p.variant_id, current + 1);
                              }}
                              className="
                                w-8 h-full
                                flex items-center justify-center
                                text-gray-600
                                bg-[#f6f6f7]
                                hover:bg-gray-200
                                active:bg-gray-300
                                transition
                                border-l border-gray-300
                              "
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </td>

                      <td>
                        {status === "out" ? (
                          <span className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded">
                            {TEXT.out}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded">
                            {TEXT.low}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
  <tr className="bg-[#f6f6f7] border-t border-[#e3e3e3]">
    <td
      colSpan="6"
      className={`px-4 py-1 ${
        lang === "EN"
          ? "rounded-br-[14px] rounded-bl-[14px]"
          : "rounded-bl-[14px] rounded-br-[14px]"
      }`}
    >
      <div
        className={`flex items-center justify-between text-sm text-gray-600 ${
          lang === "AR" ? "flex-row-reverse" : ""
        }`}
      >
        {/* COUNT */}
        <span className="text-gray-500 text-sm">
          {filtered.length} {lang === "EN" ? "items" : "عنصر"}
        </span>

        {/* PAGINATION */}
        <div className="flex items-center gap-0.5">

          {/* PREV */}
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`w-7 h-7 flex font-bold items-center justify-center rounded-md border bg-gray-200
            ${
              page === 1
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-gray-300"
            }`}
          >
            {"<"}
          </button>

          {/* NEXT */}
          <button
            onClick={() =>
              setPage((p) => Math.min(totalPages, p + 1))
            }
            disabled={page === totalPages}
            className={`w-7 h-7 flex items-center font-bold justify-center rounded-md border bg-gray-200
            ${
              page === totalPages
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-gray-300"
            }`}
          >
            {">"}
          </button>

          {/* PAGE INFO */}
          <span className="mx-2 text-gray-600 font-bold">
            {page}-{totalPages || 1}
          </span>

        </div>
      </div>
    </td>
  </tr>
</tfoot>
            </table>
          </div>

{/* MOBILE INVENTORY (SHOPIFY STYLE) */}
<div className="md:hidden space-y-3">

  {loading ? (
   <div className="space-y-3">
  {[...Array(4)].map((_, i) => (
    <div key={i} className="bg-white rounded-xl p-4 shadow animate-pulse space-y-3">

      <div className="flex justify-between">
        <div className="space-y-2">
          <div className="w-28 h-3 bg-gray-200 rounded"></div>
          <div className="w-20 h-2 bg-gray-200 rounded"></div>
        </div>
        <div className="w-16 h-5 bg-gray-200 rounded"></div>
      </div>

      <div className="w-32 h-2 bg-gray-200 rounded"></div>

      <div className="flex justify-between">
        <div className="w-16 h-3 bg-gray-200 rounded"></div>
        <div className="w-12 h-3 bg-gray-200 rounded"></div>
      </div>

      <div className="w-full h-9 bg-gray-200 rounded"></div>

    </div>
  ))}
</div>
  ) : paginated.length === 0 ? (
    <div className="flex flex-col items-center justify-center py-10 text-gray-500 gap-2">

  <FaBoxOpen className="text-2xl opacity-40" />

  <span className="text-sm">
    {TEXT.noResults}
  </span>

</div>
  ) : (
    paginated.map((p) => {
      const status = getStatus(p.stock_qty);

      return (
        <div
          key={p.variant_id}
          className="bg-white rounded-xl shadow p-4 space-y-3"
          onClick={() => p.id && navigate(`/admin/products/edit/${p.id}`)}
        >

          {/* HEADER */}
          <div className="flex items-center justify-between gap-3">

            <div>
              <div className="font-semibold text-[14px] text-gray-900">
                {lang === "AR" ? p.name_ar || p.name_en : p.name_en}
              </div>

              <div className="text-xs text-gray-400">
                {p.public_id}
              </div>
            </div>

            {/* STATUS */}
            <div>
              {status === "out" ? (
                <span className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded">
                  {TEXT.out}
                </span>
              ) : (
                <span className="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded">
                  {TEXT.low}
                </span>
              )}
            </div>

          </div>

          {/* VARIANT */}
          <div className="text-xs text-gray-500">
            {p.color_name || "-"} • {p.size || "-"}
          </div>

          {/* PRICE */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{TEXT.price}</span>
            <span className="font-medium">${p.base_price}</span>
          </div>

          {/* QUANTITY CONTROL */}
          <div onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-500">{TEXT.qty}</span>
            </div>

            <div className="flex justify-center">

              <div className={`
                flex items-center h-[36px]
                rounded-[10px]
                border overflow-hidden bg-white
                ${editedQty[p.variant_id] !== undefined
                  ? "border-amber-400 ring-2 ring-amber-100"
                  : "border-gray-300"}
              `}>

                {/* MINUS */}
                <button
                  onClick={() => {
                    const current =
                      editedQty[p.variant_id] !== undefined
                        ? Number(editedQty[p.variant_id] || 0)
                        : Number(p.stock_qty || 0);

                    handleQtyChange(p.variant_id, Math.max(0, current - 1));
                  }}
                  className="w-10 h-full bg-gray-100"
                >
                  −
                </button>

                {/* INPUT */}
                <input
                  type="number"
                  value={
                    editedQty[p.variant_id] !== undefined
                      ? editedQty[p.variant_id]
                      : p.stock_qty
                  }
                  onChange={(e) =>
                    handleQtyChange(p.variant_id, e.target.value)
                  }
                  className="w-[70px] text-center outline-none"
                />

                {/* PLUS */}
                <button
                  onClick={() => {
                    const current =
                      editedQty[p.variant_id] !== undefined
                        ? Number(editedQty[p.variant_id] || 0)
                        : Number(p.stock_qty || 0);

                    handleQtyChange(p.variant_id, current + 1);
                  }}
                  className="w-10 h-full bg-gray-100"
                >
                  +
                </button>

              </div>

            </div>
          </div>
          

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-2 border-t">

            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSelect(p.variant_id);
              }}
              className={`text-sm ${
                selected.includes(p.variant_id)
                  ? "text-black font-semibold"
                  : "text-gray-400"
              }`}
            >
              ✓
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(p.variant_id);
              }}
              className="text-red-500 text-sm"
            >
              {TEXT.delete}
            </button>

          </div>

        </div>
      );
    })
  )}

{!loading && filtered.length > 0 && totalPages > 1 && (
  <div
    className={`
      flex items-center justify-between
      mt-4 px-2 text-sm
      ${lang === "AR" ? "flex-row-reverse" : ""}
    `}
  >

    {/* PREV */}
    <button
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      disabled={page === 1}
      className={`
        px-3 py-1 border rounded-md bg-white
        ${page === 1 ? "opacity-40" : ""}
      `}
    >
      {TEXT.prev}
    </button>

    {/* PAGE INFO */}
    <span className="font-medium">
      {page} / {totalPages}
    </span>

    {/* NEXT */}
    <button
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      disabled={page === totalPages}
      className={`
        px-3 py-1 border rounded-md bg-white
        ${page === totalPages ? "opacity-40" : ""}
      `}
    >
      {TEXT.next}
    </button>

  </div>
)}

</div>


{toast && (
  <div className="fixed top-6 right-6 z-[70] animate-fade-in">
    <div className="min-w-[320px] max-w-[420px] rounded-[14px] border border-[#cde7d8] bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] overflow-hidden">

      <div className="flex items-start gap-3 px-4 py-3">

        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#e3f1df] text-[#2e7d32]">
          ✓
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#202223]">
            {lang === "AR"
              ? "تم تحديث الكميات"
              : "Quantities updated"}
          </div>

          <div className="mt-1 text-sm text-[#6d7175] space-y-[2px] max-h-[120px] overflow-auto">
            {toast.items?.slice(0, 5).map((item, i) => (
              <div key={i}>
                • {item.name} ({item.color}/{item.size}) → {item.qty}
              </div>
            ))}

            {toast.items?.length > 5 && (
              <div className="text-gray-400 text-xs">
                +{toast.items.length - 5} {TEXT.more}...
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  </div>
)}

          {/* DELETE MODAL */}
          {deleteId && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
              <div className="bg-white p-6 rounded-xl w-80">
                <h2 className="text-lg font-bold text-red-600 mb-2">
                  {TEXT.deleteTitle}
                </h2>
                <p className="mb-5">{TEXT.deleteText}</p>

                <div className="flex justify-end gap-3">
                  <button onClick={() => setDeleteId(null)}>
                    {TEXT.cancel}
                  </button>
                  <button onClick={confirmDelete}>
                    {TEXT.delete}
                  </button>
                </div>
              </div>
            </div>
          )}

{confirmSave && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl w-80">
      <h2 className="text-lg font-bold mb-2">
        {lang === "AR" ? "تأكيد الحفظ" : "Confirm Save"}
      </h2>

      <p className="mb-5 text-sm text-gray-600">
        {lang === "AR"
          ? `سيتم تحديث ${Object.keys(editedQty).length} عنصر`
          : `You are about to update ${Object.keys(editedQty).length} items`}
      </p>

      <div className="flex justify-end gap-3">
        <button onClick={() => setConfirmSave(false)}
           className="px-4 py-1 bg-gray-200 rounded-md">
          {TEXT.cancel}
        </button>

        <button
          onClick={handleBulkSave}
          className="px-4 py-1 bg-black text-white rounded-md"
        >
          {lang === "AR" ? "تأكيد" : "Confirm"}
        </button>
      </div>
    </div>
  </div>
)}

{bulkEditOpen && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

    <div className="w-full max-w-sm bg-white rounded-xl shadow-xl overflow-hidden">

      {/* HEADER */}
      <div className="px-5 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-900">
          {lang === "AR" ? "تعديل الكمية" : "Bulk edit quantity"}
        </h2>
      </div>

      {/* BODY */}
      <div className="p-5 space-y-4">

        <p className="text-sm text-gray-600">
          {lang === "AR"
            ? `سيتم تطبيق هذه الكمية على ${selected.length} عنصر`
            : `This will apply to ${selected.length} items`}
        </p>

        <input
          type="number"
          min="0"
          value={bulkQty}
          onChange={(e) => setBulkQty(e.target.value)}
          placeholder={lang === "AR" ? "أدخل الكمية" : "Enter quantity"}
          className="
            w-full px-3 py-2
            border border-gray-300
            rounded-md
            text-sm
            focus:outline-none
            focus:ring-2
            focus:ring-gray-200
          "
        />

      </div>

      {/* FOOTER */}
      <div className="px-5 py-4 border-t flex justify-end gap-3">

        <button
          onClick={() => setBulkEditOpen(false)}
          className="px-4 py-2 border rounded-md text-sm"
        >
          {TEXT.cancel}
        </button>

        <button
          onClick={handleBulkEditApply}
          className="px-4 py-2 bg-black text-white rounded-md text-sm"
        >
          {lang === "AR" ? "تطبيق" : "Apply"}
        </button>

      </div>

    </div>
  </div>
)}

{/* MOBILE SAVE BAR */}
{isDirty && (
  <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
    <div className="bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">

      <div className="flex items-center justify-between">

        {/* TEXT */}
        <span className="text-sm text-gray-600">
          {lang === "AR" ? "تغييرات غير محفوظة" : "Unsaved changes"}
        </span>

        {/* ACTIONS */}
        <div className="flex gap-2">

          {/* DISCARD */}
          <button
            onClick={handleCancelChanges}
            className="px-3 py-1 text-sm border rounded-md bg-gray-100"
          >
            {lang === "AR" ? "إلغاء" : "Discard"}
          </button>

          {/* SAVE */}
          <button
            onClick={() => setConfirmSave(true)}
            className="px-3 py-1 text-sm rounded-md bg-black text-white"
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>

        </div>

      </div>

    </div>
  </div>
)}

        </main>
      </div>
    </div>
  );
}

export default AdminInventory;